import { useState } from "react";
import {
    HeartPulse,
    Users,
    Gavel,
    Briefcase,
    ScrollText,
    Landmark,
    ArrowRight,
} from "lucide-react";
import { Reveal } from "./Reveal";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../ui/dialog";

const AREAS = [
    {
        id: "personal-injury",
        icon: HeartPulse,
        title: "Personal Injury",
        category: "Delict & Claims",
        desc: "Guidance on claims arising from motor accidents, public liability, and wrongful injury under South African law.",
        detail:
            "Under South African law, personal injury claims may arise from motor vehicle accidents (including claims against the Road Accident Fund), medical negligence, or injuries sustained through the actions of others. [Placeholder] A detailed description of the firm's approach to personal injury matters will be published once confirmed. Please contact the firm to discuss whether your matter falls within its areas of practice.",
    },
    {
        id: "family-law",
        icon: Users,
        title: "Family Law",
        category: "Domestic Relations",
        desc: "Sensitive counsel for divorce, care and contact of children, and maintenance matters.",
        detail:
            "Family law in South Africa spans divorce proceedings, care and contact arrangements for children under the Children's Act, and maintenance disputes. These matters require both legal precision and sensitivity. [Placeholder] A detailed description of the firm's family law services will be published once confirmed. Please contact the firm to discuss your circumstances in confidence.",
    },
    {
        id: "criminal-law",
        icon: Gavel,
        title: "Criminal Law",
        category: "Defence & Bail",
        desc: "Defence representation protecting constitutional rights during investigation and trial.",
        detail:
            "South African criminal procedure provides important protections for accused persons, including the right to legal representation and the right to apply for bail. [Placeholder] A detailed description of the firm's criminal defence services will be published once confirmed. Please contact the firm promptly if you require assistance with a criminal matter.",
    },
    {
        id: "business-law",
        icon: Briefcase,
        title: "Business & Commercial Law",
        category: "Corporate & Contracts",
        desc: "Contract drafting, commercial compliance, and advisory support for businesses.",
        detail:
            "Commercial matters in South Africa commonly involve contract drafting and review, company compliance under the Companies Act 71 of 2008, and the resolution of business disputes. [Placeholder] A detailed description of the firm's commercial services will be published once confirmed. Please contact the firm to discuss your business's legal requirements.",
    },
    {
        id: "estate-planning",
        icon: ScrollText,
        title: "Estate Planning",
        category: "Wills & Trusts",
        desc: "Careful preparation of wills, estate administration, and trust structures.",
        detail:
            "Estate planning in South Africa includes the drafting of valid wills, the formation of trusts, and the administration of deceased estates under the Administration of Estates Act. [Placeholder] A detailed description of the firm's estate planning services will be published once confirmed. Please contact the firm to discuss your planning needs.",
    },
    {
        id: "civil-litigation",
        icon: Landmark,
        title: "Civil Litigation",
        category: "Magistrate's & High Court",
        desc: "Representation in contractual, debt, and property disputes before the courts.",
        detail:
            "Civil litigation in South Africa may proceed in the Magistrate's Courts or the High Court, depending on the nature and value of the dispute, and covers contractual claims, debt recovery, and property disputes. [Placeholder] A detailed description of the firm's litigation practice will be published once confirmed. Please contact the firm to discuss your dispute.",
    },
];

const PracticeAreas = () => {
    const [active, setActive] = useState(null);

    return (
        <section id="practice-areas" className="bg-[#F6F5F2] py-24 lg:py-32">
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
                <Reveal className="max-w-2xl">
                    <p className="font-mono-label text-[11px] tracking-[0.3em] uppercase text-[#8E6C2D] mb-5">
                        Practice Areas
                    </p>
                    <h2 className="font-serif-display text-3xl lg:text-4xl font-medium tracking-tight text-[#0B192C] leading-[1.2]">
                        Areas of legal practice
                    </h2>
                    <p className="mt-5 text-base text-slate-600 leading-relaxed">
                        The areas below are listed as placeholders and are subject to confirmation.
                        Please contact the firm to establish whether your particular matter falls
                        within its current areas of practice.
                    </p>
                </Reveal>

                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {AREAS.map((a, i) => (
                        <Reveal key={a.id} delay={(i % 3) * 0.08}>
                            <article
                                data-testid={`practice-card-${a.id}`}
                                className="h-full bg-white border border-slate-200/80 border-t-2 border-t-[#B89047]/70 p-8 flex flex-col shadow-[0_2px_12px_-4px_rgba(11,25,44,0.04)] hover:shadow-[0_14px_30px_-8px_rgba(11,25,44,0.1)] hover:-translate-y-1 transition-all duration-300"
                            >
                                <a.icon size={26} strokeWidth={1.4} className="text-[#B89047]" aria-hidden="true" />
                                <p className="mt-5 font-mono-label text-[10px] tracking-[0.22em] uppercase text-slate-400">
                                    {a.category}
                                </p>
                                <h3 className="mt-2 font-serif-display text-xl font-medium text-[#0B192C]">
                                    {a.title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-500 leading-relaxed flex-1">{a.desc}</p>
                                <button
                                    data-testid={`practice-learn-more-${a.id}`}
                                    onClick={() => setActive(a)}
                                    className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold tracking-wide text-[#8E6C2D] hover:text-[#0B192C] transition-colors duration-300 cursor-pointer self-start"
                                >
                                    Learn More
                                    <ArrowRight size={15} aria-hidden="true" />
                                </button>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>

            <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
                <DialogContent
                    data-testid="practice-detail-dialog"
                    className="bg-white border-slate-200 max-w-lg"
                >
                    {active && (
                        <DialogHeader>
                            <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#8E6C2D]">
                                {active.category}
                            </p>
                            <DialogTitle className="font-serif-display text-2xl font-medium text-[#0B192C]">
                                {active.title}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-slate-600 leading-relaxed pt-2">
                                {active.detail}
                            </DialogDescription>
                        </DialogHeader>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
};

export default PracticeAreas;
